import React, {
  forwardRef,
  HTMLProps,
  PropsWithRef,
  useCallback,
  useMemo,
  useState,
} from 'react';
import dt from 'date-and-time';

import Header from './SimpleCalenderHeader';
import DateButton from './new-date-button';
import {
  getDatesOfMonth,
  getDaysOfWeek,
  getMonthNumberFromName,
} from './methods';
import { WeekStartDay } from './types';

import './styles.css';
import { getCurrentDate } from 'core/utils/constants/DateTime';

export type DatePickerProps = {
  /**
   * This function is called when the selected date is changed.
   */
  onChange: (date: Date) => void;
  /**
   * The selected date.
   */
  value: Date;
  /**
   * The minimum date that can be selected (inclusive).
   * Default is 1st January 1900
   */
  minDate?: Date;
  /**
   * The maximum date that can be selected (inclusive).
   * Default is 100 years from now
   */
  maxDate?: Date;
  /**
   * Week starts from which day
   */
  weekStartsFrom?: WeekStartDay | any;

  attendanceList?: any;
  /**
   * If the DatePicker is disabled
   */
  disabled?: boolean;
  isSelectionDisable?: boolean
} & PropsWithRef<
  Omit<
    HTMLProps<HTMLInputElement>,
    'onChange' | 'selected' | 'options' | 'value' | 'type' | 'name' | 'disabled'
  >
>;

/**
 * DatePicker component to pick dates
 */
export const Simplecalender = forwardRef<HTMLDivElement, DatePickerProps>(
  (
    {
      onChange,
      value = new Date(),
      minDate,
      maxDate,
      weekStartsFrom = 'Sunday',
      className,
      disabled = false,
      ...props
    },
    ref
  ) => {
    const minDateValue = useMemo(
      () => minDate?.valueOf() ?? new Date(1900, 0, 1).valueOf(),
      [minDate]
    );
    const maxDateValue = useMemo(
      () => maxDate?.valueOf() ?? dt.addYears(new Date(), 100).valueOf(),
      [maxDate]
    );

    // current month and year the user is viewing
    const [openedDate, setOpenedDate] = useState<Date>(value);

    const todayDate = useCallback(
      () => setOpenedDate((d) => new Date()),
      [setOpenedDate]
    );

    const nextMonth = useCallback(
      () => setOpenedDate((d) => dt.addMonths(d, 1)),
      [setOpenedDate]
    );

    const prevMonth = useCallback(
      () => setOpenedDate((d) => dt.addMonths(d, -1)),
      [setOpenedDate]
    );

    const onMonthChange = useCallback(
      (month: string) => {
        setOpenedDate(
          (d) =>
            new Date(
              d.getFullYear(),
              getMonthNumberFromName(month),
              d.getDate()
            )
        );
      },
      [setOpenedDate]
    );

    const onYearChange = useCallback(
      (year: number) => {
        setOpenedDate((d) => new Date(year, d.getMonth(), d.getDate()));
      },
      [setOpenedDate]
    );

    const handleClick = useCallback((d: Date) => {
      if(!props?.isSelectionDisable) {
        onChange(d)
      } else if(props?.isSelectionDisable && d.getMonth() === (new Date()).getMonth()) {
        onChange(d)
      }
    }, [onChange]);

    const daysOfWeekElements = useMemo(
      () =>
        getDaysOfWeek(weekStartsFrom).map((v) => (
          <p
            key={v}
            className={`sdp--text ${disabled ? 'sdp--text__inactive' : ''}`}
          >
            {v}
          </p>
        )),
      [weekStartsFrom, disabled]
    );

    const daysOfMonthList = useMemo(
      () =>
        getDatesOfMonth(openedDate, minDateValue, maxDateValue, weekStartsFrom),
      [openedDate, minDateValue, maxDateValue, weekStartsFrom]
    );

    function mergeArrays(a, b) {
      // Create a map from array 'a' with dates as keys for easy lookup
      const aMap = new Map(a?.map(item => [getCurrentDate(item.date, true), item]));
      // Loop through array 'b' and merge items with the same date
      for (const itemB of b) {
        const dateKey = itemB.Date;
        if (aMap.has(dateKey)) {
          const itemA: any = aMap.get(dateKey);
          itemA.Status = itemB?.Status;
        }
      }
      // Convert the map values back to an array
      const mergedArray = Array.from(aMap.values());
      return mergedArray;
    }

    function renderStatusColor(status) {
      if (status === "PRESENT")
        return "green"
      else if (status === "HALFDAY")
        return "#AD55DA"
      else if (status === "LEAVE")
        return "#EE8F35"
      else if (status === "ABSENT")
        return "#E83934"
      return ""
    }

    React.useMemo(() => {
      onChange(openedDate);
    }, [openedDate.getMonth()]);

    function checkit() {
      if(!props?.isSelectionDisable) {
        return true
      } else if(props?.isSelectionDisable && openedDate.getMonth() === (new Date()).getMonth()) {
        return true
      }
      return false
    }

    return (
      <div>
        <div className="flex justify-between items-center">
          <div>
            <button
              onClick={todayDate}
              className="font-bold text-primary-main"
            >Today</button>
          </div>
          <Header
            month={openedDate.getMonth()}
            year={openedDate.getFullYear()}
            minDateValue={minDateValue}
            maxDateValue={maxDateValue}
            nextMonth={nextMonth}
            prevMonth={prevMonth}
            onYearChange={onYearChange}
            onMonthChange={onMonthChange}
            disabled={disabled}
          />
        </div>
        <div
          className={`sdp ${className ?? ''} ${disabled ? 'sdp--disabled' : ''}`}
          aria-label="Date Picker"
          tabIndex={disabled ? -1 : 0}
          ref={ref}
          {...props}
        >

          <div className="sdp--grid">{daysOfWeekElements}</div>
          <div className="sdp--grid">
            {mergeArrays(daysOfMonthList, (props?.attendanceList || [])).map(({ date, active, ms, Status }: any) => (
              <DateButton
                status={renderStatusColor(Status)}
                key={ms}
                date={date}
                active={active && !disabled}
                selected={checkit() ? dt.isSameDay(value, date) : false}
                onClick={handleClick}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }
);

Simplecalender.displayName = 'Simplecalender';